import React, { useCallback, useState } from "react";

import useDefaultCustomDomain from "@agent-ui/features/page-settings/useDefaultCustomDomain";
import { usePublish } from "@agent-ui/hooks/usePublish";
import { Alert } from "@replo/design-system/blocks/alert/Alert";
import { Button } from "@replo/design-system/components/button/Button";
import { Modal } from "@replo/design-system/components/modal/Modal";
import {
  PopoverContent,
  PopoverDescription,
  PopoverFooter,
  PopoverHeader,
  PopoverRoot,
  PopoverTitle,
  PopoverTrigger,
} from "@replo/design-system/components/popover/Popover";
import { Textarea } from "@replo/design-system/components/textarea/Textarea";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { useReploAgent } from "@v2/contexts/ReploAgentContext";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { ArrowUpRight, ChevronDown, ChevronUp } from "lucide-react";
import { PuffLoader } from "react-spinners";

const DebugLogsAlert: React.FC<{
  onViewLogs: () => void;
}> = ({ onViewLogs }) => {
  return (
    <Alert
      variant="error"
      description="[Debug] Error publishing"
      action={{ text: "View logs", onClick: onViewLogs }}
    />
  );
};

const DebugLogsModal: React.FC<{
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  logs: {
    build?: { stdout: string; stderr: string };
    publish?: { stdout: string; stderr: string };
  } | null;
}> = ({ isOpen, onOpenChange, logs }) => {
  const buildLogs = logs?.build;
  const publishLogs = logs?.publish;
  const title = publishLogs ? "Debug Logs (publish)" : "Debug Logs (build)";
  const displayLogs = publishLogs ?? buildLogs;

  return (
    <Modal open={isOpen} onOpenChange={onOpenChange} title={title} size="lg">
      <div className="flex flex-col gap-2 w-full">
        <Textarea
          value={displayLogs?.stdout ?? ""}
          className="w-full"
          readOnly
          UNSAFE_className="h-[200px] font-mono text-2xs"
        />
        <Textarea
          value={displayLogs?.stderr ?? ""}
          className="w-full"
          readOnly
          UNSAFE_className="h-[200px] font-mono text-2xs"
        />
      </div>
    </Modal>
  );
};

export const PublishButton = ({
  publishedSiteUrl,
  isViewableLive,
}: {
  publishedSiteUrl?: string;
  isViewableLive: boolean;
}) => {
  const { user } = useCurrentUser();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isDebugModalOpen, setIsDebugModalOpen] = useState(false);
  const [debugLogs, setDebugLogs] = useState<{
    build?: { stdout: string; stderr: string };
    publish?: { stdout: string; stderr: string };
  } | null>(null);
  const { project } = useCurrentProjectContext();
  const logEvent = useLogAnalytics();
  const { state } = useReploAgent();
  const {
    publish,
    isPublishing: isPublishPending,
    getPublishLogs,
  } = usePublish();

  const defaultCustomDomain = useDefaultCustomDomain();
  const showAddDomainButton = !Boolean(defaultCustomDomain);

  const publishStatus = state.publishStatus;
  const isPublishing =
    publishStatus?.status === "building" ||
    publishStatus?.status === "publishing";
  const isPublished = publishStatus?.status === "published";
  const isFailed = publishStatus?.status === "failed";

  const handlePublish = useCallback(() => {
    publish();
    logEvent("editor.nav.publish.click");
  }, [publish, logEvent]);

  const handleViewDebugLogs = useCallback(async () => {
    const logs = await getPublishLogs();
    setDebugLogs(logs);
    setIsDebugModalOpen(true);
  }, [getPublishLogs]);

  if (!project) {
    return null;
  }

  const siteUrlWithoutProtocol = publishedSiteUrl?.replace("https://", "");
  const faviconUrl =
    project.shopMetadataLightModeFaviconUrl ??
    project.shopMetadataDarkModeFaviconUrl ??
    undefined;

  return (
    <>
      <PopoverRoot open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger
          render={
            <Button
              variant="primary"
              size="base"
              endEnhancer={isPopoverOpen ? <ChevronUp /> : <ChevronDown />}
            />
          }
        >
          Publish
        </PopoverTrigger>

        <PopoverContent
          side="bottom"
          align="end"
          sideOffset={8}
          showCloseButton={false}
          className="w-96"
        >
          {isPublishing ? (
            <>
              <PopoverHeader>
                <PopoverTitle>
                  {publishStatus?.status === "publishing"
                    ? "Publishing..."
                    : "Building..."}
                </PopoverTitle>
                <PopoverDescription>
                  Feel free to leave this tab.
                </PopoverDescription>
              </PopoverHeader>
              <PublishingLoadingContent />
            </>
          ) : (
            <>
              {isPublished && <PublishSuccessContent />}
              {isFailed && (
                <PublishFailedContent
                  isSuperuser={Boolean(user?.isSuperuser)}
                  onViewLogs={() => void handleViewDebugLogs()}
                />
              )}
              <LinkToPublishedPage
                siteUrl={publishedSiteUrl}
                siteUrlWithoutProtocol={siteUrlWithoutProtocol}
                isViewableLive={isViewableLive}
                faviconUrl={faviconUrl}
              />
              <PopoverFooter>
                <PublishPopoverFooter
                  isPublishing={isPublishing || isPublishPending}
                  handlePublish={handlePublish}
                  showAddDomainButton={showAddDomainButton}
                />
              </PopoverFooter>
            </>
          )}
        </PopoverContent>
      </PopoverRoot>
      <DebugLogsModal
        isOpen={isDebugModalOpen}
        onOpenChange={setIsDebugModalOpen}
        logs={debugLogs}
      />
    </>
  );
};

const PublishingLoadingContent: React.FC = () => {
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-32 h-32">
        <img
          src="/images/shops/Globe.png"
          alt="Globe"
          className="absolute top-1/2 left-1/2 w-16 h-16 -translate-x-[56%] -translate-y-[56%] z-10"
        />
        <PuffLoader color="#2563EB" size={120} speedMultiplier={0.75} />
      </div>
    </div>
  );
};

const PublishSuccessContent: React.FC = () => {
  return (
    <div className="flex flex-col items-center">
      <div className="w-72 h-32 flex items-center justify-center">
        <img
          src="/images/shops/successful-publish.png"
          alt="Successful publish"
          className="w-64"
        />
      </div>
    </div>
  );
};

const PublishFailedContent: React.FC<{
  isSuperuser: boolean;
  onViewLogs: () => void;
}> = ({ isSuperuser, onViewLogs }) => {
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-48 h-32">
        <img
          src="/images/shops/failed-publish.png"
          alt="Failed publish"
          className="absolute top-1/2 left-1/2 w-80 -translate-x-[50%] -translate-y-[50%] z-10"
        />
      </div>
      <p className="text-sm text-muted text-center">
        Publishing failed. Please try again.
      </p>
      {isSuperuser && <DebugLogsAlert onViewLogs={onViewLogs} />}
    </div>
  );
};

const LinkToPublishedPage: React.FC<{
  siteUrl?: string;
  siteUrlWithoutProtocol?: string;
  isViewableLive: boolean;
  faviconUrl?: string;
}> = ({ siteUrl, siteUrlWithoutProtocol, isViewableLive, faviconUrl }) => {
  return (
    <div className="flex flex-col gap-4 p-3 border-border border-0.5 rounded-lg bg-sheet w-full">
      {siteUrl && (
        <Tooltip content={siteUrlWithoutProtocol} triggerAsChild>
          <button
            onClick={
              isViewableLive
                ? () => {
                    window.open(siteUrl, "_blank");
                  }
                : undefined
            }
            className={twMerge(
              "w-full flex items-center justify-between gap-1.5 bg-transparent border-none cursor-pointer text-left",
              isViewableLive ? "text-default" : "text-muted",
              !isViewableLive && "cursor-not-allowed",
            )}
            disabled={!isViewableLive}
          >
            <span className="flex items-center gap-1.5 min-w-0">
              <img
                src={
                  faviconUrl ??
                  "https://shops-metadata.replocdn.com/default-favicon-light-mode.ico"
                }
                alt={faviconUrl ? "Site favicon" : "Default site favicon"}
                className="w-4 h-4 flex-shrink-0"
              />
              <span
                className="truncate hover:underline text-sm text-left"
                style={{ direction: "rtl" }}
              >
                {siteUrlWithoutProtocol}
              </span>
            </span>
            {isViewableLive && (
              <ArrowUpRight className="w-4 h-4 flex-shrink-0" />
            )}
          </button>
        </Tooltip>
      )}
    </div>
  );
};

const PublishPopoverFooter: React.FC<{
  isPublishing: boolean;
  handlePublish: () => void;
  showAddDomainButton: boolean;
}> = ({ isPublishing, handlePublish, showAddDomainButton }) => {
  const { project } = useCurrentProjectContext();

  const handleAddDomain = () => {
    if (project?.id) {
      window.location.href = `/shops/${project.id}/settings/shop/general`;
    }
  };

  return (
    <div className="flex gap-3 w-full">
      {showAddDomainButton && (
        <Button
          variant="secondary"
          size="base"
          onClick={handleAddDomain}
          className="flex-1"
        >
          Add Domain
        </Button>
      )}
      <Button
        variant="primary"
        size="base"
        onClick={handlePublish}
        loading={isPublishing}
        className="flex-1"
      >
        Publish
      </Button>
    </div>
  );
};
